import { datadogRum } from '@datadog/browser-rum';

type User = {
  id: string;
  districtId: string;
  district: string;
};

type DatadogContext = Record<string, unknown>;

// Session Replay Privacy Utilities
function applySessionReplayPrivacy(): void {
  // Auto-apply privacy attributes to common PII form fields
  const piiSelectors = [
    'input[type="email"]',
    'input[name*="email"]',
    'input[id*="email"]',
    'input[name*="name"]',
    'input[id*="name"]',
    'input[name*="firstName"]',
    'input[name*="lastName"]',
    'input[name*="phone"]',
    'input[name*="ssn"]',
    'input[name*="address"]',
    'input[name*="zip"]',
    'input[name*="dob"]',
    'input[name*="password"]',
    'input[type="password"]',
    'input[name*="studentId"]',
    'input[name*="teacherId"]',
    'textarea[name*="comment"]',
    'textarea[name*="feedback"]',
    'textarea[name*="note"]',
    '.student-info',
    '.teacher-info',
    '.personal-info',
    '.contact-info'
  ];

  piiSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(element => {
      if (!element.hasAttribute('data-dd-privacy')) {
        element.setAttribute('data-dd-privacy', 'mask');
      }
    });
  });
}

function observeAndMaskNewElements(): void {
  // Create a MutationObserver to automatically mask new PII elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          
          // Check if the new element or its children need masking
          const elementsToMask = [
            element.matches('input[type="email"], input[name*="email"], input[name*="name"], input[name*="phone"], input[type="password"]') ? element : null,
            ...Array.from(element.querySelectorAll('input[type="email"], input[name*="email"], input[name*="name"], input[name*="phone"], input[type="password"], .student-info, .teacher-info'))
          ].filter(Boolean);

          elementsToMask.forEach(el => {
            if (el && !el.hasAttribute('data-dd-privacy')) {
              el.setAttribute('data-dd-privacy', 'mask');
            }
          });
        }
      });
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function init(): void {
  datadogRum.init({
    applicationId: import.meta.env.VITE_APP_DATADOG_APP_ID,
    clientToken: import.meta.env.VITE_APP_DATADOG_CLIENT_ID,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: import.meta.env.VITE_APP_DATADOG_SITE,
    service: import.meta.env.VITE_APP_DATADOG_SERVICE,
    env: import.meta.env.VITE_APP_DATADOG_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: import.meta.env.NODE_ENV === 'production' ? 20 : 100,
    
    // Privacy and PII Protection Settings for Session Replay
    defaultPrivacyLevel: 'mask-user-input',
    enablePrivacyForActionName: true,
    
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    startSessionReplayRecordingManually: true,
    trackSessionAcrossSubdomains: true,
  });
  
  // Set global context
  datadogRum.setGlobalContextProperty('application', 'vizzle-react');
  datadogRum.setGlobalContextProperty('tier', 'frontend');
  datadogRum.setGlobalContextProperty('platform', 'web');

  // Apply session replay privacy attributes to existing and new elements
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applySessionReplayPrivacy();
      observeAndMaskNewElements();
    });
  } else {
    applySessionReplayPrivacy();
    observeAndMaskNewElements();
  }
}

function setUser(user: User): void {
  // Only send non-PII user data to Datadog
  datadogRum.setUser({
    id: user.id, // Use non-PII ID (should be hashed or anonymized)
    name: user.id, // Use ID instead of actual name to avoid PII
    districtId: user.districtId, // District ID is usually safe
    districtName: user.district, // District name is usually safe
  });
  datadogRum.startSessionReplayRecording();
}

function userLogout(): void {
  datadogRum.clearUser();
  datadogRum.stopSession();
}

function addAction(name: string, context?: DatadogContext): void {
  datadogRum.addAction(name, context);
}

function addError(error: Error, context?: DatadogContext): void {
  datadogRum.addError(error, context);
}

function setGlobalContext(key: string, value: unknown): void {
  datadogRum.setGlobalContextProperty(key, value);
}

function addTiming(name: string, time?: number): void {
  datadogRum.addTiming(name, time);
}

export default {
  init,
  setUser,
  userLogout,
  addAction,
  addError,
  setGlobalContext,
  addTiming,
  applySessionReplayPrivacy,
  observeAndMaskNewElements,
};
